// src/components/NavBar.jsx
import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Menu,
  X,
  ChevronDown,
  User,
  LogOut,
  ShoppingCart,
  ChevronRight,
  Settings,
  Info,
  Mail,
  Newspaper,
  DollarSign,
  Truck,
  Factory,
  PlayCircle,
  Instagram,
  Facebook,
} from "lucide-react";
import { preloadRoute } from "../../routes/routePreloader";
import api from "../../utils/axios";
import { toast } from "react-hot-toast";
import SparePartsDropdown from "../../components/SparePartsDropdown";

const preloadProps = (path) => ({
  onMouseEnter: () => void preloadRoute(path),
  onFocus: () => void preloadRoute(path),
  onTouchStart: () => void preloadRoute(path),
});

const defaultAvatar =
  "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSgB730p0ChSl_CNr5N6n05AGzEtEAhFypOFg&s";

/*
|--------------------------------------------------------------------------
| Navigation in-memory cache
|--------------------------------------------------------------------------
| Normal React navigation / remount:
|   -> cached user/categories are reused, so APIs are not called again.
|
| Hard browser refresh:
|   -> JavaScript memory resets. Auth user is refreshed once (only if a
|      token exists). Categories are fetched lazily on first menu use.
|
| Login/logout:
|   -> auth-changed updates the NavBar instantly without a browser refresh,
|      keeping other page caches (for example Machines) alive.
|--------------------------------------------------------------------------
*/
let cachedNavUser;
let cachedNavCategories;
let userRequest = null;
let categoriesRequest = null;

const readStoredUser = () => {
  if (typeof window === "undefined") return null;

  try {
    const raw = localStorage.getItem("user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    localStorage.removeItem("user");
    return null;
  }
};

const formatNavUser = (userData) => {
  if (!userData) return null;

  return {
    name: userData.name || "User",
    email: userData.email || "",
    image: userData.image || defaultAvatar,
  };
};

const NavBar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [user, setUser] = useState(() => {
    if (typeof window === "undefined") return null;
    if (!localStorage.getItem("token")) return null;
    return formatNavUser(readStoredUser());
  });
  const [categories, setCategories] = useState(() =>
    Array.isArray(cachedNavCategories) ? cachedNavCategories : [],
  );
  const [loadingCats, setLoadingCats] = useState(
    () => cachedNavCategories === undefined,
  );
  const [profileDropdown, setProfileDropdown] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);
  const [moreDropdown, setMoreDropdown] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Check if current route is active.
  const isActive = (path) => {
    if (path === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(path);
  };

  // Refresh authenticated user only once per hard browser refresh.
  // During normal SPA navigation the module cache is reused.
  useEffect(() => {
    let cancelled = false;

    const loadUser = async () => {
      const token = localStorage.getItem("token");

      // Logged-out visitors should never call /users/me.
      if (!token) {
        cachedNavUser = null;
        setUser(null);
        return;
      }

      api.defaults.headers.common.Authorization = `Bearer ${token}`;

      // Show local user immediately while the one refresh request runs.
      const storedUser = readStoredUser();
      if (storedUser && !cancelled) {
        setUser(formatNavUser(storedUser));
      }

      // Already resolved during this JS session/navigation.
      if (cachedNavUser !== undefined) {
        if (!cancelled) setUser(cachedNavUser);
        return;
      }

      try {
        // Prevent duplicate requests (including React StrictMode remounts).
        if (!userRequest) {
          userRequest = api.get("/users/me");
        }

        const res = await userRequest;
        const userData =
          res.data?.data?.user || res.data?.data || res.data || null;

        if (!userData) {
          throw new Error("User not found");
        }

        const formattedUser = formatNavUser(userData);
        cachedNavUser = formattedUser;

        localStorage.setItem("user", JSON.stringify(userData));

        if (!cancelled) {
          setUser(formattedUser);
        }
      } catch (err) {
        // Only clear the auth session when the server confirms it is invalid.
        if (err?.response?.status === 401) {
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          sessionStorage.removeItem("sym_user_profile");
          delete api.defaults.headers.common.Authorization;
          cachedNavUser = null;

          if (!cancelled) setUser(null);
          return;
        }

        // For temporary network/server errors, keep the saved user visible.
        const fallbackUser = formatNavUser(readStoredUser());
        cachedNavUser = fallbackUser;

        if (!cancelled) {
          setUser(fallbackUser);
        }

        console.error(
          "Failed to refresh navbar user:",
          err?.response?.data?.message || err?.message || err,
        );
      } finally {
        userRequest = null;
      }
    };

    loadUser();

    return () => {
      cancelled = true;
    };
  }, []);

  // Login.jsx dispatches auth-changed after saving token/user.
  // This updates Sign In -> Profile immediately without window.reload().
  useEffect(() => {
    const applyCurrentAuth = (eventUser = null) => {
      const token = localStorage.getItem("token");

      if (!token) {
        cachedNavUser = null;
        setUser(null);
        setProfileDropdown(false);
        return;
      }

      api.defaults.headers.common.Authorization = `Bearer ${token}`;

      const sourceUser = eventUser || readStoredUser();
      const formattedUser = formatNavUser(sourceUser);

      if (formattedUser) {
        cachedNavUser = formattedUser;
        setUser(formattedUser);
      }
    };

    const handleAuthChanged = (event) => {
      applyCurrentAuth(event?.detail?.user || null);
    };

    // Keeps auth UI correct when another browser tab logs in/out.
    const handleStorage = (event) => {
      if (event.key === "token" || event.key === "user") {
        applyCurrentAuth();
      }
    };

    window.addEventListener("auth-changed", handleAuthChanged);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("auth-changed", handleAuthChanged);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  // Categories are intentionally lazy: no /categories request on every page
  // load. First range/mobile-menu use fetches once, then memory cache is reused
  // until the next hard browser refresh.
  const ensureCategories = async () => {
    if (cachedNavCategories !== undefined) {
      setCategories(cachedNavCategories);
      setLoadingCats(false);
      return cachedNavCategories;
    }

    setLoadingCats(true);

    try {
      if (!categoriesRequest) {
        categoriesRequest = api.get("/categories");
      }

      const res = await categoriesRequest;
      const rawData = res.data?.data ?? res.data ?? [];
      const data = Array.isArray(rawData)
        ? rawData
        : Array.isArray(rawData?.categories)
          ? rawData.categories
          : [];

      cachedNavCategories = data;
      setCategories(data);
      return data;
    } catch (err) {
      console.error(
        "Failed to load categories:",
        err?.response?.data?.message || err?.message || err,
      );

      // Cache the failure for this SPA session to avoid request loops.
      cachedNavCategories = [];
      setCategories([]);
      return [];
    } finally {
      categoriesRequest = null;
      setLoadingCats(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    sessionStorage.removeItem("sym_user_profile");

    cachedNavUser = null;
    userRequest = null;

    delete api.defaults.headers.common.Authorization;

    setUser(null);
    setProfileDropdown(false);
    setMobileOpen(false);

    // Notify every mounted auth-dependent component in the same tab.
    window.dispatchEvent(
      new CustomEvent("auth-changed", {
        detail: { user: null, token: null },
      }),
    );

    toast.success("Logged out successfully!");
    navigate("/", { replace: true });
  };

  return (
    <>
      <nav className="w-full bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl shadow-2xl shadow-black/5 dark:shadow-black/30 sticky top-0 z-50 border-b border-gray-200/50 dark:border-gray-800/50">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 py-2 lg:px-8">
          {/* Premium Logo */}
          <Link to="/" 
           {...preloadProps("/")}
           className="flex items-center gap-3 group">
            <div className="relative">
              {/* <div className="absolute inset-0 bg-gradient-to-br from-blue-500 via-amber-500 to-orange-500 rounded-2xl blur-xl opacity-70 group-hover:opacity-100 transition-opacity duration-500" /> */}
              <img
                src="https://symforklift.nyc3.cdn.digitaloceanspaces.com/logo.webp"
                alt="SYM Logo"
                width="96"
                height="64"
                loading="eager"
                fetchPriority="high"
                decoding="async"
                className="relative h-16 w-16 sm:h-18 sm:w-24 object-contain transform group-hover:scale-105 transition-transform duration-300"
              />
            </div>
          </Link>
          {/* In the desktop menu section */}
          <div className="hidden md:flex items-center gap-6 lg:gap-10">
            <div className="relative dark:text-white">
              <SparePartsDropdown isActive={isActive} />
            </div>

            {/* Premium Navigation Links */}
            <div className="relative group hidden lg:block">
              <Link
                to="/freight"
                {...preloadProps("/freight")}
                className={`relative font-semibold tracking-wide text-sm lg:text-md transition-all duration-300 ${
                  isActive("/freight")
                    ? "text-yellow-600 dark:text-yellow-400"
                    : "text-gray-700 dark:text-gray-300 hover:text-yellow-500 dark:hover:text-yellow-400"
                }`}
              >
                <span className="relative flex items-center gap-1.5">
                  <span
                    className={`absolute inset-0 bg-gradient-to-r from-yellow-500/10 via-amber-500/10 to-orange-500/10 rounded-lg blur-xl transition-all duration-500 ${
                      isActive("/freight")
                        ? "opacity-100 scale-105"
                        : "opacity-0 group-hover:opacity-100 group-hover:scale-105"
                    }`}
                  />

                  <span className="relative flex items-center gap-1.5 px-1">
                    <Truck className="w-5 h-5 group-hover:scale-110 transition-transform duration-300" />
                    Freight
                    <ChevronDown className="w-4 h-4 transition-transform duration-300 group-hover:rotate-180" />
                  </span>
                </span>

                <span
                  className={`absolute -bottom-1 left-0 h-0.5 bg-gradient-to-r from-yellow-500 via-amber-500 to-orange-500 rounded-full transition-all duration-500 ${
                    isActive("/freight") ? "w-full" : "w-0 group-hover:w-full"
                  }`}
                />
              </Link>

              {/* Dropdown */}
              <div className="absolute top-full left-0 mt-3 w-64 bg-white dark:bg-gray-900 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50 overflow-hidden">
                <Link
                  to="/freight/login"                  
                  className="flex items-center gap-3 px-4 py-3 hover:bg-yellow-50 dark:hover:bg-gray-800 transition-colors"
                   {...preloadProps("/freight/login")}
                >
                  <Truck className="w-5 h-5 text-yellow-500" />
                  <div>
                    <p className="text-sm font-semibold text-gray-800 dark:text-white">
                      Company Login
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Freight company portal
                    </p>
                  </div>
                </Link>

                <Link
                  to="/freight/customer-login"
                  className="flex items-center gap-3 px-4 py-3 hover:bg-yellow-50 dark:hover:bg-gray-800 transition-colors border-t border-gray-200 dark:border-gray-700"
                  {...preloadProps("/freight/customer-login")}
                >
                  <User className="w-5 h-5 text-yellow-500" />
                  <div>
                    <p className="text-sm font-semibold text-gray-800 dark:text-white">
                      Customer Login
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Customer portal
                    </p>
                  </div>
                </Link>
              </div>
            </div>
            {/* Premium Range Dropdown - Forklift Machinery */}
            <div
              className="relative group"
              onMouseEnter={() => {
                setActiveCategory(true);
                ensureCategories();
                void preloadRoute("/machines");
              }}
              onMouseLeave={() => setActiveCategory(null)}
            >
              <button
                className={`flex items-center gap-2 font-semibold tracking-wide text-sm lg:text-md transition-all duration-300 ${
                  isActive("/machines") || activeCategory
                    ? "text-yellow-600 dark:text-yellow-400"
                    : "text-gray-700 dark:text-gray-300 hover:text-yellow-500 dark:hover:text-yellow-400"
                }`}
                {...preloadProps("/machines")}
                onClick={() => navigate("/machines")}
              >
                {/* Premium Forklift Icon with Animation */}
                <span className="relative">
                  <span className="text-lg transform group-hover:scale-110 transition-transform duration-300 inline-block">
                    {/* <Factory className="w-6 h-6 text-black" /> */}
                  </span>
                  {activeCategory && (
                    <span className="absolute -top-1 -right-2 w-2 h-2 bg-yellow-500 rounded-full animate-ping" />
                  )}
                </span>
                Our Range
                <ChevronDown
                  className={`w-4 h-4 transition-transform duration-300 ${
                    activeCategory ? "rotate-180 text-yellow-500" : ""
                  }`}
                />
              </button>

              {/* Premium Dropdown Menu */}
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-72 bg-white dark:bg-gray-900 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50 overflow-hidden">
                {/* Header */}
                <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-yellow-50 dark:bg-yellow-900/10">
                  <div className="p-2 bg-yellow-500 rounded-lg">
                    <Factory className="w-5 h-5 text-white" />
                  </div>

                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                      Forklift Categories
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Select a category
                    </p>
                  </div>
                </div>

                {/* Categories */}
                <div className="py-2">
                  {loadingCats ? (
                    <div className="px-4 py-6 text-center text-sm text-gray-500">
                      Loading...
                    </div>
                  ) : categories.length > 0 ? (
                    categories.slice(0, 5).map((cat) => (
                      <button
                        key={cat._id}
                        onClick={() => {
                          navigate(`/machines?category=${cat._id}`);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-yellow-50 dark:hover:bg-gray-800 transition-colors"
                      >
                        <span className="flex-1 text-sm font-medium text-gray-700 dark:text-gray-200">
                          {cat.name}
                        </span>

                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      </button>
                    ))
                  ) : (
                    <div className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                      No categories available
                    </div>
                  )}
                </div>
              </div>
            </div>

            <Link
              to="/videos"
              {...preloadProps("/videos")}
              className={`relative font-semibold hidden lg:block tracking-wide text-sm lg:text-md transition-all duration-300 ${
                isActive("/videos")
                  ? "text-yellow-600 dark:text-yellow-400"
                  : "text-gray-700 dark:text-gray-300 hover:text-yellow-500 dark:hover:text-yellow-400"
              } group`}
            >
              <span className="relative flex items-center gap-2">
                {/* Animated Video Icon */}
                <span className="relative">
                  <span className="text-lg transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 inline-block">
                    <PlayCircle className="w-5 h-5" />
                  </span>
                </span>

                <span className="relative">
                  Gallery
                  {/* Animated Glow */}
                  <span className="absolute -inset-2 bg-gradient-to-r from-red-500/10 via-yellow-500/10 to-red-500/10 rounded-lg blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </span>
              </span>
              {/* Animated Underline with Video Theme */}
              <span
                className={`absolute -bottom-1 left-0 h-0.5 bg-gradient-to-r from-red-500 via-yellow-500 to-red-500 rounded-full transition-all duration-500 ${
                  isActive("/videos") ? "w-full" : "w-0 group-hover:w-full"
                }`}
              />
            </Link>

            {/* Premium "More" Dropdown */}
            <div
              className="relative group"
              onMouseEnter={() => setMoreDropdown(true)}
              onMouseLeave={() => setMoreDropdown(false)}
            >
              <button
                className={`flex items-center gap-1.5 font-semibold tracking-wide text-sm lg:text-md transition-all duration-300 ${
                  moreDropdown ||
                  isActive("/about") ||
                  isActive("/contact") ||
                  isActive("/blogs") ||
                  isActive("/instagram-updates") ||
                  isActive("/facebook-updates") ||
                  isActive("/finance")
                    ? "text-yellow-600 dark:text-yellow-400"
                    : "text-gray-700 dark:text-gray-300 hover:text-yellow-500 dark:hover:text-yellow-400"
                }`}
              >
                <span className="relative">
                  {moreDropdown && (
                    <span className="absolute -top-1 -right-1 w-2 h-2 bg-yellow-500 rounded-full animate-ping" />
                  )}
                </span>
                More Pages
                <ChevronDown
                  className={`w-4 h-4 transition-transform duration-300 ${
                    moreDropdown ? "rotate-180 text-yellow-500" : ""
                  }`}
                />
              </button>

              {/* More Dropdown Menu */}
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-4 w-64 bg-white dark:bg-gray-900 rounded-2xl shadow-2xl shadow-black/20 dark:shadow-black/50 border border-gray-200/80 dark:border-gray-800/80 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform -translate-y-2 group-hover:translate-y-0 z-50 overflow-hidden">
                {/* Animated Border */}
                <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-yellow-400 via-amber-500 to-orange-500 animate-pulse" />
                {/* Menu Items */}
                <div className="p-2">
                  {/* About Us */}
                  <Link
                    to="/about"
                    className="flex items-center gap-3 p-3 rounded-xl hover:bg-gradient-to-r hover:from-yellow-50/50 hover:to-amber-50/30 dark:hover:from-gray-800 dark:hover:to-gray-800/50 transition-all duration-200 group/item"
                    {...preloadProps("/about")}
                  >
                    <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900/30 dark:to-blue-800/30 flex items-center justify-center group-hover/item:scale-110 transition-transform duration-300">
                      <Info className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-800 dark:text-gray-200 group-hover/item:text-yellow-600 dark:group-hover/item:text-yellow-400 transition-colors">
                        About Us
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Learn about our company
                      </p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400 group-hover/item:text-yellow-500 group-hover/item:translate-x-1 transition-all duration-300" />
                  </Link>

                  {/* Blogs */}
                  <Link
                    to="/blogs"
                    className="flex items-center gap-3 p-3 rounded-xl hover:bg-gradient-to-r hover:from-yellow-50/50 hover:to-amber-50/30 dark:hover:from-gray-800 dark:hover:to-gray-800/50 transition-all duration-200 group/item"
                    {...preloadProps("/blogs")}
                  >
                    <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-gradient-to-br from-purple-100 to-purple-200 dark:from-purple-900/30 dark:to-purple-800/30 flex items-center justify-center group-hover/item:scale-110 transition-transform duration-300">
                      <Newspaper className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-800 dark:text-gray-200 group-hover/item:text-yellow-600 dark:group-hover/item:text-yellow-400 transition-colors">
                        Blogs
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Latest articles & insights
                      </p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400 group-hover/item:text-yellow-500 group-hover/item:translate-x-1 transition-all duration-300" />
                  </Link>

                  {/* Instagram Updates */}
                  <Link
                    to="/instagram-updates"
                    className="flex items-center gap-3 p-3 rounded-xl hover:bg-gradient-to-r hover:from-yellow-50/50 hover:to-amber-50/30 dark:hover:from-gray-800 dark:hover:to-gray-800/50 transition-all duration-200 group/item"
                  >
                    <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-gradient-to-br from-pink-100 to-orange-100 dark:from-pink-900/30 dark:to-orange-900/30 flex items-center justify-center group-hover/item:scale-110 transition-transform duration-300">
                      <Instagram className="w-4 h-4 text-pink-600 dark:text-pink-400" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-800 dark:text-gray-200 group-hover/item:text-yellow-600 dark:group-hover/item:text-yellow-400 transition-colors">
                        Instagram Updates
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Our latest photos and reels
                      </p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400 group-hover/item:text-yellow-500 group-hover/item:translate-x-1 transition-all duration-300" />
                  </Link>

                  {/* Facebook Updates */}
                  <Link
                    to="/facebook-updates"
                    className="flex items-center gap-3 p-3 rounded-xl hover:bg-blue-50/60 dark:hover:bg-gray-800 transition-all duration-200 group/item"
                  >
                    <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                      <Facebook className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-800 dark:text-gray-200">Facebook Updates</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Our latest Facebook posts</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </Link>

                  {/* Finance */}
                  <Link
                    to="/finance"
                    className="flex items-center gap-3 p-3 rounded-xl hover:bg-gradient-to-r hover:from-yellow-50/50 hover:to-amber-50/30 dark:hover:from-gray-800 dark:hover:to-gray-800/50 transition-all duration-200 group/item"
                    {...preloadProps("/finance")}
                  >
                    <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-gradient-to-br from-green-100 to-green-200 dark:from-green-900/30 dark:to-green-800/30 flex items-center justify-center group-hover/item:scale-110 transition-transform duration-300">
                      <DollarSign className="w-4 h-4 text-green-600 dark:text-green-400" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-800 dark:text-gray-200 group-hover/item:text-yellow-600 dark:group-hover/item:text-yellow-400 transition-colors">
                        Finance
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Financing & payment options
                      </p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400 group-hover/item:text-yellow-500 group-hover/item:translate-x-1 transition-all duration-300" />
                  </Link>

                  {/* Contact Us */}
                  <Link
                    to="/contact"
                    className="flex items-center gap-3 p-3 rounded-xl hover:bg-gradient-to-r hover:from-yellow-50/50 hover:to-amber-50/30 dark:hover:from-gray-800 dark:hover:to-gray-800/50 transition-all duration-200 group/item"
                    {...preloadProps("/contact")}
                  >
                    <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-gradient-to-br from-red-100 to-red-200 dark:from-red-900/30 dark:to-red-800/30 flex items-center justify-center group-hover/item:scale-110 transition-transform duration-300">
                      <Mail className="w-4 h-4 text-red-600 dark:text-red-400" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-800 dark:text-gray-200 group-hover/item:text-yellow-600 dark:group-hover/item:text-yellow-400 transition-colors">
                        Contact Us
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Get in touch with us
                      </p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400 group-hover/item:text-yellow-500 group-hover/item:translate-x-1 transition-all duration-300" />
                  </Link>
                </div>
              </div>
            </div>
            {/* Theme Toggle (Premium) */}
            {/* <button
              onClick={toggleTheme}
              className="p-2.5 rounded-xl bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900 hover:from-yellow-100 hover:to-amber-100 dark:hover:from-yellow-900/20 dark:hover:to-amber-900/20 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 group mr-3"
              aria-label="Toggle theme"
            >
              {theme === "dark" ? (
                <Sun className="w-5 h-5 text-yellow-500 group-hover:rotate-180 transition-transform duration-500" />
              ) : (
                <Moon className="w-5 h-5 text-gray-700 group-hover:rotate-180 transition-transform duration-500" />
              )}
            </button> */}
          </div>

          {/* Theme Toggle (Mobile/Tablet Only) */}
          {/* <button
            onClick={toggleTheme}
            className="p-2.5 rounded-xl bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900 hover:from-yellow-100 hover:to-amber-100 dark:hover:from-yellow-900/20 dark:hover:to-amber-900/20 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 group mr-3 lg:hidden md:hidden"
            aria-label="Toggle theme"
          >
            {theme === "dark" ? (
              <Sun className="w-5 h-5 text-yellow-500 group-hover:rotate-180 transition-transform duration-500" />
            ) : (
              <Moon className="w-5 h-5 text-gray-700 group-hover:rotate-180 transition-transform duration-500" />
            )}
          </button> */}

          {/* Premium User Profile or Login */}
          {user ? (
            <div className="relative">
              <button
                onClick={() => setProfileDropdown(!profileDropdown)}
                className="flex items-center gap-3 p-2 rounded-2xl hover:bg-gradient-to-r hover:from-yellow-50/50 hover:to-amber-50/30 dark:hover:from-gray-800 dark:hover:to-gray-800/50 transition-all duration-300 group"
              >
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-yellow-500 to-amber-500 rounded-full blur-md opacity-70 group-hover:opacity-100 transition-opacity" />
                  <img
                    src={user.image || defaultAvatar}
                    alt={user.name}
                    width="40"
                    height="40"
                    decoding="async"
                    className="relative w-9 h-9 sm:w-10 sm:h-10 rounded-full object-cover ring-2 ring-yellow-500/50 group-hover:ring-yellow-500 transition-all duration-300"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = defaultAvatar;
                    }}
                  />
                </div>
                <div className="text-left hidden xl:block">
                  <p className="text-sm font-bold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
                    {user.name}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    My Account
                  </p>
                </div>
                <ChevronDown
                  className={`w-4 h-4 text-gray-500 transition-transform duration-300 ${
                    profileDropdown ? "rotate-180 text-yellow-500" : ""
                  }`}
                />
              </button>

              {/* Premium Profile Dropdown */}
              {profileDropdown && (
                <div className="absolute right-0 mt-3 w-64 bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl rounded-2xl shadow-2xl shadow-black/20 dark:shadow-black/50 border border-gray-200/80 dark:border-gray-800/80 overflow-hidden z-50 animate-fadeIn">
                  {/* Animated Border */}
                  <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-yellow-500 via-amber-500 to-orange-500" />

                  <div className="p-2">
                    <div className="px-4 py-3 border-b border-gray-200/50 dark:border-gray-800/50">
                      <p className="text-sm font-bold text-gray-900 dark:text-white">
                        {user.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {user.email}
                      </p>
                    </div>

                    <div className="py-2">
                      <Link
                        to="/profile"
                        onClick={() => setProfileDropdown(false)}
                        className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-gradient-to-r hover:from-yellow-50/50 hover:to-amber-50/30 dark:hover:from-gray-800 dark:hover:to-gray-800/50 transition-all duration-200 group/item"
                        {...preloadProps("/profile")}
                      >
                        <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg group-hover/item:bg-yellow-200 dark:group-hover/item:bg-yellow-900/50 transition-colors">
                          <User className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
                        </div>
                        <span className="text-sm font-medium text-gray-800 dark:text-gray-300">
                          My Profile
                        </span>
                      </Link>

                      <Link
                        to="/profile"
                        onClick={() => setProfileDropdown(false)}
                        className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-gradient-to-r hover:from-yellow-50/50 hover:to-amber-50/30 dark:hover:from-gray-800 dark:hover:to-gray-800/50 transition-all duration-200 group/item"
                        {...preloadProps("/profile")}
                      >
                        <div className="p-2 bg-gray-100 dark:bg-gray-800 rounded-lg group-hover/item:bg-gray-200 dark:group-hover/item:bg-gray-700 transition-colors">
                          <Settings className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                        </div>
                        <span className="text-sm font-medium text-gray-800 dark:text-gray-300">
                          Settings
                        </span>
                      </Link>

                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-gradient-to-r hover:from-red-50/50 hover:to-red-50/30 dark:hover:from-red-900/20 dark:hover:to-red-900/10 transition-all duration-200 group/item mt-2"
                      >
                        <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg group-hover/item:bg-red-200 dark:group-hover/item:bg-red-900/50 transition-colors">
                          <LogOut className="w-4 h-4 text-red-600 dark:text-red-400" />
                        </div>
                        <span className="text-sm font-medium text-red-600 dark:text-red-400">
                          Logout
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="hidden lg:block items-center  gap-3">
              <Link
                to="/login"
                className="relative px-6 py-2.5 text-sm font-semibold text-gray-800 dark:text-gray-300 rounded-xl border-2 border-gray-300 dark:border-gray-700 hover:border-yellow-500 dark:hover:border-yellow-500 overflow-hidden group transition-all duration-300"
                {...preloadProps("/login")}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-yellow-100/30 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
                Sign In
              </Link>
            </div>
          )}

          {/* Premium Cart Button */}
          <Link
            to="/my-orders"
            className="relative p-2.5 rounded-xl  bg-gradient-to-br from-yellow-500 to-amber-500 hover:from-yellow-600 hover:to-amber-600 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 group"

            aria-label="Shopping Cart"
            {...preloadProps("/my-orders")}
          >
            <ShoppingCart className="w-5 h-5 text-black" />
          </Link>

          <button
            onClick={() => {
              setMobileOpen(true);
              ensureCategories();
            }}
            className="p-2.5 rounded-xl bg-gradient-to-br lg:hidden from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900 shadow-lg hover:shadow-xl transition-all duration-300"
          >
            <Menu className="w-6 h-6 text-gray-900 dark:text-white" />
          </button>
        </div>
      </nav>

      {/* Premium Mobile Menu Drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          {/* Animated Overlay */}
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setMobileOpen(false)}
          />

          {/* Drawer with Slide-in Animation */}
          <div
            className="
  absolute right-0 top-0
  w-full sm:w-96
  h-full
  pointer-events-auto
  bg-gradient-to-b from-white via-white to-gray-50
  dark:from-gray-900 dark:via-gray-900 dark:to-gray-950
  shadow-2xl shadow-black/30
"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Premium Header */}
            <div className="p-6 border-b border-gray-200/50 dark:border-gray-800/50 bg-gradient-to-r from-white to-yellow-50/30 dark:from-gray-900 dark:to-yellow-900/5">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <img
                    src="https://symforklift.nyc3.cdn.digitaloceanspaces.com/logo.webp"
                    alt="SYM Logo"
                    width="40"
                    height="40"
                    decoding="async"
                    className="h-10 w-10 object-contain"
                  />
                  <div></div>
                </div>
                <button
                  onClick={() => setMobileOpen(false)}
                  className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200"
                >
                  <X className="w-6 h-6 text-gray-600 dark:text-gray-400" />
                </button>
              </div>
            </div>

            {/* User Profile Section */}
            {user && (
              <div className="p-6 border-b border-gray-200/50 dark:border-gray-800/50 bg-gradient-to-r from-yellow-50/50 to-amber-50/30 dark:from-yellow-900/10 dark:to-amber-900/5">
                <div className="flex items-center gap-4 mb-4">
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-yellow-500 to-amber-500 rounded-full blur-md opacity-60" />
                    <img
                      src={user.image || defaultAvatar}
                      alt={user.name}
                      width="56"
                      height="56"
                      decoding="async"
                      className="relative w-14 h-14 rounded-full object-cover ring-2 ring-yellow-500/50"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = defaultAvatar;
                      }}
                    />
                  </div>
                  <div>
                    <p className="font-bold text-gray-900 dark:text-white text-lg">
                      {user.name}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                      {user.email}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Link
                    to="/profile"
                    onClick={() => setMobileOpen(false)}
                    className="text-center py-3 bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-600 hover:to-amber-600 text-black font-bold rounded-xl text-sm transition-all duration-300"
                    {...preloadProps("/profile")}
                  >
                    Profile
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="py-3 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-bold rounded-xl text-sm transition-all duration-300"
                  >
                    Logout
                  </button>
                </div>
              </div>
            )}

            {/* Navigation Links */}
            <div className="p-6">
              <div className="space-y-1">
                <Link
                  to="/"
                  onClick={() => setMobileOpen(false)}
                  className={`flex items-center gap-4 p-4 rounded-xl transition-all duration-200 ${
                    isActive("/")
                      ? "bg-gradient-to-r from-yellow-50 to-amber-50/50 dark:from-yellow-900/20 dark:to-amber-900/10 text-yellow-600 dark:text-yellow-400"
                      : "hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100/50 dark:hover:from-gray-800 dark:hover:to-gray-800/50"
                  }`}
                  {...preloadProps("/")}
                >
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      isActive("/")
                        ? "bg-yellow-500 text-white"
                        : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400"
                    }`}
                  >
                    <span className="text-lg">🏠</span>
                  </div>
                  <span className="font-semibold dark:text-white">Home</span>
                </Link>

                <Link
                  to="/about"
                  onClick={() => setMobileOpen(false)}
                  className={`flex items-center gap-4 p-4  rounded-xl transition-all duration-200 ${
                    isActive("/about")
                      ? "bg-gradient-to-r from-yellow-50 to-amber-50/50 dark:from-yellow-900/20 dark:to-amber-900/10 text-yellow-600 dark:text-yellow-400"
                      : "hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100/50 dark:hover:from-gray-800 dark:hover:to-gray-800/50"
                  }`}
                  {...preloadProps("/about")}
                >
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      isActive("/about")
                        ? "bg-yellow-500 text-white"
                        : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400"
                    }`}
                  >
                    <span className="text-lg">🏢</span>
                  </div>
                  <span className="font-semibold dark:text-white">
                    About Us
                  </span>
                </Link>

                <Link
                  to="/machines"
                  onClick={() => setMobileOpen(false)}
                  className={`flex items-center gap-4 p-4 rounded-xl transition-all duration-200 ${
                    isActive("/machines")
                      ? "bg-gradient-to-r from-yellow-50 to-amber-50/50 dark:from-yellow-900/20 dark:to-amber-900/10 text-yellow-600 dark:text-yellow-400"
                      : "hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100/50 dark:hover:from-gray-800 dark:hover:to-gray-800/50"
                  }`}
                  {...preloadProps("/machines")}
                >
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      isActive("/machines")
                        ? "bg-yellow-500 text-white"
                        : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400"
                    }`}
                  >
                    {/* <Factory className="w-5 h-5" /> */}
                  </div>
                  <span className="font-semibold dark:text-white">
                    Our Range
                  </span>
                  <ChevronDown className="w-4 h-4 ml-auto" />
                </Link>

                <Link
                  to="/instagram-updates"
                  onClick={() => setMobileOpen(false)}
                  className={`flex items-center gap-4 p-4 rounded-xl transition-all duration-200 ${
                    isActive("/instagram-updates")
                      ? "bg-gradient-to-r from-yellow-50 to-amber-50/50 dark:from-yellow-900/20 dark:to-amber-900/10 text-yellow-600 dark:text-yellow-400"
                      : "hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100/50 dark:hover:from-gray-800 dark:hover:to-gray-800/50"
                  }`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    isActive("/instagram-updates")
                      ? "bg-yellow-500 text-white"
                      : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400"
                  }`}>
                    <Instagram className="w-5 h-5" />
                  </div>
                  <span className="font-semibold dark:text-white">Instagram Updates</span>
                </Link>

                <Link
                  to="/facebook-updates"
                  onClick={() => setMobileOpen(false)}
                  className={`flex items-center gap-4 p-4 rounded-xl transition-all duration-200 ${
                    isActive("/facebook-updates")
                      ? "bg-gradient-to-r from-blue-50 to-blue-100/50 dark:from-blue-900/20 text-blue-600 dark:text-blue-400"
                      : "hover:bg-gray-50 dark:hover:bg-gray-800"
                  }`}
                >
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-blue-100 dark:bg-blue-900/20 text-blue-600">
                    <Facebook className="w-5 h-5" />
                  </div>
                  <span className="font-semibold dark:text-white">Facebook Updates</span>
                </Link>

                <Link
                  to="/blogs"
                  onClick={() => setMobileOpen(false)}
                  className={`flex items-center gap-4 p-4 rounded-xl transition-all duration-200 ${
                    isActive("/blogs")
                      ? "bg-gradient-to-r from-yellow-50 to-amber-50/50 dark:from-yellow-900/20 dark:to-amber-900/10 text-yellow-600 dark:text-yellow-400"
                      : "hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100/50 dark:hover:from-gray-800 dark:hover:to-gray-800/50"
                  }`}
                  {...preloadProps("/blogs")}
                >
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      isActive("/blogs")
                        ? "bg-yellow-500 text-white"
                        : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400"
                    }`}
                  >
                    <span className="text-lg">📰</span>
                  </div>
                  <span className="font-semibold dark:text-white">Blogs</span>
                </Link>

                <Link
                  to="/freight"
                  onClick={() => setMobileOpen(false)}
                  className={`flex items-center gap-4 p-4 rounded-xl transition-all duration-200 ${
                    isActive("/freight")
                      ? "bg-gradient-to-r from-yellow-50 to-amber-50/50 dark:from-yellow-900/20 dark:to-amber-900/10 text-yellow-600 dark:text-yellow-400"
                      : "hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100/50 dark:hover:from-gray-800 dark:hover:to-gray-800/50"
                  }`}
                  {...preloadProps("/freight")}
                >
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-300 ${
                      isActive("/freight")
                        ? "bg-gradient-to-r from-yellow-500 to-amber-500 text-white shadow-lg shadow-yellow-500/30"
                        : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 group-hover:bg-gradient-to-r group-hover:from-yellow-500/20 group-hover:to-amber-500/20"
                    }`}
                  >
                    <Truck className="w-5 h-5" />
                  </div>
                  <span className="font-semibold dark:text-white flex items-center gap-2">
                    Freight
                    <span className="text-[10px] font-bold text-white bg-gradient-to-r from-yellow-500 to-amber-500 px-1.5 py-0.5 rounded-full animate-pulse">
                      NEW
                    </span>
                  </span>
                </Link>
                <Link
                  to="/finance"
                  onClick={() => setMobileOpen(false)}
                  className={`flex items-center gap-4 p-4 rounded-xl transition-all duration-200 ${
                    isActive("/finance")
                      ? "bg-gradient-to-r from-yellow-50 to-amber-50/50 dark:from-yellow-900/20 dark:to-amber-900/10 text-yellow-600 dark:text-yellow-400"
                      : "hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100/50 dark:hover:from-gray-800 dark:hover:to-gray-800/50"
                  }`}
                  {...preloadProps("/finance")}
                >
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      isActive("/finance")
                        ? "bg-yellow-500 text-white"
                        : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400"
                    }`}
                  >
                    <span className="text-lg">💰</span>
                  </div>
                  <span className="font-semibold dark:text-white">Finance</span>
                </Link>
                <Link
                  to="/videos"
                  onClick={() => setMobileOpen(false)}
                  className={`flex items-center gap-4 p-4 rounded-xl transition-all duration-200 ${
                    isActive("/videos")
                    ? "bg-gradient-to-r from-yellow-50 to-amber-50/50 dark:from-yellow-900/20 dark:to-amber-900/10 text-yellow-600 dark:text-yellow-400"
                    : "hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100/50 dark:hover:from-gray-800 dark:hover:to-gray-800/50"
                  }`}
                  {...preloadProps("/videos")}
                  
                >
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      isActive("/videos")
                        ? "bg-yellow-500 text-white"
                        : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400"
                    }`}
                  >
                    <span className="text-lg">▶</span>
                  </div>
                  <span className="font-semibold dark:text-white">Gallery</span>
                </Link>
                <Link
                  to="/spareparts"
                  onClick={() => setMobileOpen(false)}
                  className={`flex items-center gap-4 p-4 rounded-xl transition-all duration-200 ${
                    isActive("/spareparts")
                    ? "bg-gradient-to-r from-yellow-50 to-amber-50/50 dark:from-yellow-900/20 dark:to-amber-900/10 text-yellow-600 dark:text-yellow-400"
                    : "hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100/50 dark:hover:from-gray-800 dark:hover:to-gray-800/50"
                  }`}
                  {...preloadProps("/spareparts")}
                >
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      isActive("/spareparts")

                        ? "bg-yellow-500 text-white"
                        : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400"
                    }`}
                  >
                    <span className="text-lg">🛠</span>
                  </div>
                  <span className="font-semibold dark:text-white">
                    Spare Parts
                  </span>
                </Link>
                <Link
                  to="/contact"
                  onClick={() => setMobileOpen(false)}
                  className={`flex items-center gap-4 p-4 rounded-xl transition-all duration-200 ${
                    isActive("/contact")
                    ? "bg-gradient-to-r from-yellow-50 to-amber-50/50 dark:from-yellow-900/20 dark:to-amber-900/10 text-yellow-600 dark:text-yellow-400"
                    : "hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100/50 dark:hover:from-gray-800 dark:hover:to-gray-800/50"
                  }`}
                  {...preloadProps("/contact")}
                >
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      isActive("/contact")
                        ? "bg-yellow-500 text-white"
                        : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400"
                    }`}
                  >
                    <span className="text-lg">📞</span>
                  </div>
                  <span className="font-semibold dark:text-white">
                    Contact Us
                  </span>
                </Link>
              </div>

              {/* Categories Section */}
              <div className="mt-8">
                <h3 className="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">
                  Product Categories
                </h3>
                <div className="space-y-2">
                  {categories.slice(0, 5).map((cat) => (
                    <Link
                      key={cat._id}
                      to={`/machines?category=${cat._id}`}
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center justify-between p-3 rounded-xl hover:bg-gradient-to-r hover:from-yellow-50/50 hover:to-amber-50/30 dark:hover:from-gray-800 dark:hover:to-gray-800/50 transition-all duration-200"
                      {...preloadProps(`/machines?category=${cat._id}`)}
                      >
                      <span className="text-sm font-medium text-gray-800 dark:text-gray-300">
                        {cat.name}
                      </span>
                      {/* Optional arrow */}
                      <span className="text-gray-400">→</span>
                    </Link>
                  ))}
                </div>
              </div>

              {!user && (
                <div className="mt-8 pt-8 border-t border-gray-200/50 dark:border-gray-800/50 space-y-4">
                  <Link
                    to="/login"
                    onClick={() => setMobileOpen(false)}
                    className="block w-full text-center py-3.5 bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-600 hover:to-amber-600 text-black font-bold rounded-xl text-base transition-all duration-300 shadow-lg hover:shadow-xl"
                    {...preloadProps("/login")}
                  >
                    Login
                  </Link>
                  <Link
                    to="/register"
                    onClick={() => setMobileOpen(false)}
                    className="block w-full text-center py-3.5 border-2 border-yellow-500 text-yellow-600 dark:text-yellow-400 font-bold rounded-xl text-base hover:bg-gradient-to-r hover:from-yellow-50 hover:to-amber-50/50 dark:hover:from-yellow-900/10 dark:hover:to-amber-900/5 transition-all duration-300"
                    {...preloadProps("/register")}
                  >
                    Create Account
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NavBar;
